import { useEffect, useRef, useState } from "react";
import Button from "./Button";
import {
  applyBookListUpdate,
  cacheBookListStatus,
  flushPendingBookListUpdates,
  getCachedBookListStatus,
  queueBookListUpdate
} from "../utils/bookListCache";
import "./AddToListsModal.css";

const CLOSE_DURATION_MS = 180;

const EMPTY_SELECTIONS = {
  bookmarks: false,
  finished: false,
  tbr: false
};

function AddToListsModal({ book, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selections, setSelections] = useState(EMPTY_SELECTIONS);
  const [bookmarkNote, setBookmarkNote] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (!book?.id) return;

    let cancelled = false;
    const loadStatus = async () => {
      setLoading(true);
      setError("");
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const userId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
        const cachedStatus = getCachedBookListStatus(userId, book.id);
        if (cachedStatus) {
          setSelections({
            bookmarks: Boolean(cachedStatus.bookmarks),
            finished: Boolean(cachedStatus.finished),
            tbr: Boolean(cachedStatus.tbr)
          });
          setBookmarkNote(cachedStatus.bookmark_note || "");
        }

        if (!navigator.onLine) return;

        const response = await fetch(
          `${apiUrl}/get-book-list-status.php?uid=${userId}&epub_id=${book.id}`,
          {
            credentials: "include",
            cache: "no-store"
          }
        );
        const data = await response.json();
        if (cancelled || !data?.success) return;
        setSelections({
          bookmarks: Boolean(data.status?.bookmarks),
          finished: Boolean(data.status?.finished),
          tbr: Boolean(data.status?.tbr)
        });
        setBookmarkNote(data.status?.bookmark_note || "");
        cacheBookListStatus(userId, book.id, {
          bookmarks: Boolean(data.status?.bookmarks),
          finished: Boolean(data.status?.finished),
          tbr: Boolean(data.status?.tbr),
          bookmark_note: data.status?.bookmark_note || ""
        });
      } catch (loadError) {
        if (!cancelled) {
          if (!getCachedBookListStatus(
            JSON.parse(localStorage.getItem("user") || "{}").id || 0,
            book.id
          )) {
            setError("Could not load list options right now.");
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [book]);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      onClose?.();
    }, CLOSE_DURATION_MS);
  };

  const toggleSelection = (key) => {
    setSelections((current) => ({
      ...current,
      [key]: !current[key]
    }));
  };

  const handleSave = async () => {
    if (!book?.id || saving) return;
    const bookmarkMissingNote = selections.bookmarks && !bookmarkNote.trim();
    const hasOtherSelections = selections.finished || selections.tbr;

    if (bookmarkMissingNote && !hasOtherSelections) {
      setError("Bookmarks need a review before they can be saved.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const userId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
      const nextStatus = {
        bookmarks: bookmarkMissingNote ? false : Boolean(selections.bookmarks),
        finished: Boolean(selections.finished),
        tbr: Boolean(selections.tbr)
      };

      if (!navigator.onLine) {
        const localResult = applyBookListUpdate({
          userId,
          book,
          status: nextStatus,
          bookmarkNote: bookmarkNote.trim()
        });
        queueBookListUpdate({
          userId,
          book,
          status: nextStatus,
          bookmarkNote: bookmarkNote.trim()
        });
        window.dispatchEvent(
          new CustomEvent("book-lists-updated", {
            detail: {
              epubId: book.id,
              status: localResult.status,
              counts: localResult.counts,
              offline: true
            }
          })
        );
        onSaved?.({
          success: true,
          status: localResult.status,
          counts: localResult.counts,
          offline: true
        });
        requestClose();
        return;
      }

      await flushPendingBookListUpdates(apiUrl);

      const form = new FormData();
      form.append("uid", String(userId));
      form.append("epub_id", book.id);
      form.append("bookmarks", bookmarkMissingNote ? "0" : selections.bookmarks ? "1" : "0");
      form.append("finished", selections.finished ? "1" : "0");
      form.append("tbr", selections.tbr ? "1" : "0");
      form.append("bookmark_note", bookmarkNote.trim());

      const response = await fetch(`${apiUrl}/save-book-lists.php`, {
        method: "POST",
        credentials: "include",
        body: form
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setError(data?.error || "Could not save lists.");
        return;
      }

      applyBookListUpdate({
        userId,
        book,
        status: data.status || nextStatus,
        bookmarkNote: bookmarkNote.trim()
      });

      window.dispatchEvent(
        new CustomEvent("book-lists-updated", {
          detail: {
            epubId: book.id,
            status: data.status,
            counts: data.counts
          }
        })
      );

      if (bookmarkMissingNote) {
        setSelections({
          bookmarks: false,
          finished: Boolean(data.status?.finished),
          tbr: Boolean(data.status?.tbr)
        });
        setError("Finished/TBR saved. Add a review if you also want this in bookmarks.");
        onSaved?.(data);
        return;
      }
      onSaved?.(data);
      requestClose();
    } catch (saveError) {
      setError("Could not save lists.");
    } finally {
      setSaving(false);
    }
  };

  if (!book) return null;

  return (
    <div className={`add-lists-overlay${isClosing ? " add-lists-overlay--closing" : ""}`} onClick={requestClose}>
      <div
        className={`add-lists-sheet${isClosing ? " add-lists-sheet--closing" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="add-lists-sheet__header">
          <div>
            <h3 className="mb-1">Add to Lists</h3>
            <p className="mb-0 add-lists-sheet__book">{book.title}</p>
          </div>
          <button type="button" className="add-lists-sheet__close" onClick={requestClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {loading ? (
          <div className="add-lists-sheet__loading">Loading options...</div>
        ) : (
          <>
            <div className="add-lists-sheet__options d-flex flex-column gap-2">
              <button
                type="button"
                className={`btn-neutral add-lists-toggle text-start${selections.bookmarks ? " add-lists-toggle--active" : ""}`}
                onClick={() => toggleSelection("bookmarks")}
              >
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <p className="mb-1 fw-medium">Bookmarks</p>
                    <p className="mb-0 add-lists-toggle__meta fw-normal">Requires a review</p>
                  </div>
                  <span className="material-symbols-rounded">
                    {selections.bookmarks ? "check_circle" : "radio_button_unchecked"}
                  </span>
                </div>
              </button>

              <button
                type="button"
                className={`btn-neutral add-lists-toggle text-start${selections.finished ? " add-lists-toggle--active" : ""}`}
                onClick={() => toggleSelection("finished")}
              >
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <p className="mb-1 fw-medium">Finished</p>
                    <p className="mb-0 fw-normal add-lists-toggle__meta">Add to your completed timeline</p>
                  </div>
                  <span className="material-symbols-rounded">
                    {selections.finished ? "check_circle" : "radio_button_unchecked"}
                  </span>
                </div>
              </button>

              <button
                type="button"
                className={`btn-neutral add-lists-toggle text-start${selections.tbr ? " add-lists-toggle--active" : ""}`}
                onClick={() => toggleSelection("tbr")}
              >
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <p className="mb-1 fw-medium">TBR</p>
                    <p className="mb-0 fw-normal add-lists-toggle__meta">Save for later</p>
                  </div>
                  <span className="material-symbols-rounded">
                    {selections.tbr ? "check_circle" : "radio_button_unchecked"}
                  </span>
                </div>
              </button>
            </div>

            {selections.bookmarks && (
              <div className="mt-3">
                <label className="add-lists-sheet__label mb-2" htmlFor="bookmark-note">
                  Review
                </label>
                <textarea
                  id="bookmark-note"
                  className="btn-neutral add-lists-sheet__textarea"
                  value={bookmarkNote}
                  onChange={(event) => setBookmarkNote(event.target.value)}
                  placeholder="Add the review that should appear in bookmarks..."
                />
              </div>
            )}

            {error && <p className="add-lists-sheet__error mb-0 mt-3">{error}</p>}

            <div className="d-flex mt-3">
              <Button variant="cta" onClick={handleSave}>
                {saving ? "Saving..." : "Save Lists"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AddToListsModal;
